import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import { Search, Shield, X } from 'lucide-react';
import { useTranslate, useTranslationStatus } from '../hooks/useTranslate';
import { useLanguage } from '../context/LanguageContext';
import { OptimizedImage } from '../components/ui/optimized-image';

const FamillesBlasons = () => {
  const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
  const [selectedLetter, setSelectedLetter] = useState('A');
  const [familles, setFamilles] = useState([]);
  const [lignages, setLignages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedFamille, setSelectedFamille] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [pageData, setPageData] = useState(null);
  const { currentLanguage } = useLanguage();
  
  // Savoir si des traductions sont en cours
  const isTranslating = useTranslationStatus();

  // Traductions des textes statiques UNIQUEMENT
  const { translatedText: titleTranslated } = useTranslate("Les Familles et Blasons");
  const { translatedText: subtitleTranslated } = useTranslate("Les Lignages de Bruxelles");
  const { translatedText: descTranslated } = useTranslate("Découvrez les blasons et l'histoire des familles descendantes des lignages de Bruxelles");
  const { translatedText: lignagesTitleTranslated } = useTranslate("Les 7 lignages");
  const { translatedText: searchTitleTranslated } = useTranslate("Recherche par initiale");
  const { translatedText: searchDescTranslated } = useTranslate("Sélectionnez une lettre pour afficher toutes les familles commençant par celle-ci");
  const { translatedText: noResultsTranslated } = useTranslate("Aucune famille trouvée pour cette lettre");
  const { translatedText: modalTitleBlasonTranslated } = useTranslate("Description héraldique");
  const { translatedText: modalTitleInfoTranslated } = useTranslate("Informations historiques");
  const { translatedText: closeButtonTranslated } = useTranslate("Fermer");
  const { translatedText: detailsButtonTranslated } = useTranslate("+ de détails");
  const { translatedText: projectTitleTranslated } = useTranslate("Projet collaboratif");
  const { translatedText: projectDescTranslated } = useTranslate("Cette collection de blasons et d'informations généalogiques est un projet collaboratif en cours. Les membres de l'association peuvent contribuer en partageant leurs recherches, blasons familiaux et documents historiques pour enrichir cette précieuse base de données du patrimoine bruxellois.");
  
  // Traductions du contenu du modal (uniquement si une famille est sélectionnée)
  const { translatedText: modalDescriptionTranslated } = useTranslate(selectedFamille?.description_blason || "");
  const { translatedText: modalAutreInfoTranslated } = useTranslate(selectedFamille?.autre_info || "");

  // Charger les données de la page (header)
  useEffect(() => {
    const fetchPageData = async () => {
      try {
        console.log('🔄 Chargement des données de la page Familles...');
        const response = await fetch(`${API_BASE}/api/site/pages/familles-blasons`);
        if (response.ok) {
          const data = await response.json();
          console.log('✅ Données Familles chargées:', data);
          setPageData(data);
        }
      } catch (error) {
        console.error('Erreur chargement page:', error);
      }
    };
    fetchPageData();
  }, []);

  // Charger les 7 lignages au montage du composant
  useEffect(() => {
    const loadLignages = async () => {
      try {
        const url = new URL('/api/familles-blasons', API_BASE);
        url.searchParams.append('letter', '7');
        const res = await fetch(url.toString());
        if (!res.ok) throw new Error('Erreur chargement des lignages');
        const data = await res.json();
        setLignages(data.familles || []);
      } catch (e) {
        console.error('Erreur:', e);
        setLignages([]);
      }
    };
    
    loadLignages();
  }, []);

  // Charger les familles selon la lettre sélectionnée
  useEffect(() => {
    const loadFamilles = async () => {
      setLoading(true);
      try {
        const url = new URL('/api/familles-blasons', API_BASE);
        url.searchParams.append('letter', selectedLetter);
        const res = await fetch(url.toString());
        if (!res.ok) throw new Error('Erreur chargement');
        const data = await res.json();
        setFamilles(data.familles || []);
      } catch (e) {
        console.error('Erreur:', e);
        setFamilles([]);
      } finally {
        setLoading(false);
      }
    };

    loadFamilles();
  }, [selectedLetter]);

  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

  return (
    <div className="min-h-screen bg-white">
      {/* Header Image Banner */}
      {pageData?.header_image && (
        <div 
          className="relative w-full overflow-hidden"
          style={{ 
            height: '600px',
            marginTop: '-80px',
            paddingTop: '80px'
          }}
        >
          <OptimizedImage 
            src={pageData.header_image.startsWith('/api/files/') || pageData.header_image.startsWith('http') ? (pageData.header_image.startsWith('http') ? pageData.header_image : `${API_BASE}${pageData.header_image}`) : `${API_BASE}/api/files/${pageData.header_image}`}
            alt={pageData.title}
            className="absolute inset-0 w-full h-full object-cover"
            priority={true}
            placeholder="blur"
          />
          <div className="absolute inset-0 bg-black bg-opacity-30"></div>
        >
          <div className="absolute inset-0 bg-black bg-opacity-30"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center" style={{ paddingTop: '100px' }}>
            <h1 
              className="text-5xl font-bold text-center px-4"
              style={{ 
                fontFamily: '"Lora", serif',
                color: 'rgb(196, 150, 96)',
                textShadow: '3px 3px 6px rgba(0,0,0,0.8), -1px -1px 2px rgba(255,255,255,0.3)'
              }}
            >
              {titleTranslated}
            </h1>
            <p 
              className="text-2xl text-center px-4 mt-3"
              style={{ 
                fontFamily: '"Lora", serif',
                color: 'white',
                textShadow: '2px 2px 4px rgba(0,0,0,0.8)'
              }}
            >
              {subtitleTranslated}
            </p>
          </div>
        </div>
      )}

      <div className="py-16" style={{ paddingTop: pageData?.header_image ? '4rem' : '8rem' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Alerte de traduction en cours */}
          {isTranslating && currentLanguage !== 'fr' && (
            <div 
              className="mb-6 bg-blue-50 border-l-4 border-blue-600 text-blue-800 px-6 py-4 rounded-r-lg shadow-md flex items-center gap-3"
              style={{ fontFamily: '"Lora", serif', position: 'relative', zIndex: 10 }}
            >
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-600 border-t-transparent"></div>
              <div>
                <p className="font-semibold">Traduction en cours...</p>
                <p className="text-sm">Le contenu s'affiche en français en attendant la traduction complète.</p>
              </div>
            </div>
          )}
          
          {!pageData?.header_image && (
            <div className="text-center mb-12">
              <h1 
                className="text-5xl font-bold mb-4"
                style={{ 
                  fontFamily: '"Lora", serif',
                  color: 'rgb(196, 150, 96)'
                }}
              >
                {titleTranslated}
              </h1>
              <p 
                className="text-gray-600"
                style={{ fontFamily: '"Lora", serif', fontSize: '17px' }}
              >
                {descTranslated}
              </p>
            </div>
          )}

          {/* Section des 7 lignages principaux */}
        <div className="mb-16">
          <h2 
            className="text-4xl font-bold text-center mb-8"
            style={{ 
              fontFamily: '"Lora", serif',
              color: 'rgb(196, 150, 96)'
            }}
          >
            {lignagesTitleTranslated}
          </h2>
          
          <div className="flex flex-wrap justify-center gap-6">
            {lignages.map((lignage, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow w-full md:w-[calc(50%-0.75rem)] lg:w-[calc(33.333%-1rem)] xl:w-[calc(25%-1.125rem)]">
                <CardContent className="p-6 text-center">
                  <div className="mb-4">
                    {lignage.blason_image_path ? (
                      <OptimizedImage 
                        src={lignage.blason_image_url_full || `${API_BASE}/api/uploads/${lignage.blason_image_path}`}
                        alt={`Blason ${lignage.nom_famille}`}
                        className="w-24 h-24 mx-auto object-contain"
                        useThumbnail={true}
                        placeholder="skeleton"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div 
                      className="w-24 h-24 mx-auto bg-gray-100 rounded flex items-center justify-center text-gray-400"
                      style={{ display: lignage.blason_image_path ? 'none' : 'flex' }}
                    >
                      <Shield size={48} />
                    </div>
                  </div>
                  <h3 
                    className="text-xl font-semibold mb-3"
                    style={{ 
                      fontFamily: '"Lora", serif',
                      color: 'rgb(196, 150, 96)'
                    }}
                  >
                    {lignage.nom_famille}
                  </h3>
                  {lignage.description_blason && (
                    <p 
                      className="text-gray-600 text-sm leading-relaxed mb-4"
                      style={{ fontFamily: '"Lora", serif' }}
                    >
                      <strong>Description:</strong> {lignage.description_blason}
                    </p>
                  )}
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="hover:bg-opacity-10"
                    onClick={() => {
                      setSelectedFamille(lignage);
                      setShowModal(true);
                    }}
                    style={{ 
                      fontFamily: '"Lora", serif',
                      borderColor: 'rgb(196, 150, 96)',
                      color: 'rgb(196, 150, 96)'
                    }}
                  >
                    + de détails
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        <Separator className="my-16" />

        {/* Section des familles descendantes */}
        <div>
          <div className="text-center mb-8">
            <h2 
              className="text-4xl font-bold mb-4"
              style={{ 
                fontFamily: '"Lora", serif',
                color: 'rgb(196, 150, 96)'
              }}
            >
              {searchTitleTranslated}
            </h2>
            <p 
              className="text-gray-600 mb-6"
              style={{ fontFamily: '"Lora", serif', fontSize: '17px' }}
            >
              {searchDescTranslated}
            </p>
          </div>

          {/* Alphabet de navigation */}
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {alphabet.map((letter) => (
              <Button
                key={letter}
                variant={selectedLetter === letter ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedLetter(letter)}
                style={{ 
                  fontFamily: '"Lora", serif',
                  backgroundColor: selectedLetter === letter ? 'rgb(196, 150, 96)' : 'transparent',
                  borderColor: 'rgb(196, 150, 96)',
                  color: selectedLetter === letter ? 'white' : 'rgb(196, 150, 96)'
                }}
              >
                {letter}
              </Button>
            ))}
          </div>

          {/* Liste des familles chargées depuis l'API */}
          {loading ? (
            <div className="text-center py-12">
              <p className="text-gray-500">Chargement...</p>
            </div>
          ) : familles.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">{noResultsTranslated}</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {familles.map((famille) => (
                <Card key={famille.id} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0">
                        {famille.blason_image_path ? (
                          <OptimizedImage 
                            src={famille.blason_image_url_full || `${API_BASE}/api/uploads/${famille.blason_image_path}`}
                            alt={`Blason ${famille.nom_famille}`}
                            className="w-16 h-16 object-contain"
                            useThumbnail={true}
                            placeholder="skeleton"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.parentElement.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div 
                          className="w-16 h-16 bg-gray-100 rounded flex items-center justify-center text-gray-400"
                          style={{ display: famille.blason_image_path ? 'none' : 'flex' }}
                        >
                          <Shield size={32} />
                        </div>
                      </div>
                      <div className="flex-1">
                        <h4 
                          className="text-lg font-semibold mb-2"
                          style={{ 
                            fontFamily: '"Lora", serif',
                            color: 'rgb(196, 150, 96)'
                          }}
                        >
                          {famille.nom_famille}
                        </h4>
                        {famille.description_blason && (
                          <p 
                            className="text-gray-600 text-sm mb-3"
                            style={{ fontFamily: '"Lora", serif' }}
                          >
                            {famille.description_blason}
                          </p>
                        )}
                        {famille.autre_info && (
                          <p 
                            className="text-gray-600 text-sm mb-3 line-clamp-3"
                            style={{ fontFamily: '"Lora", serif' }}
                          >
                            {famille.autre_info.substring(0, 150) + '...'}
                          </p>
                        )}
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="mt-2"
                          onClick={() => {
                            setSelectedFamille(famille);
                            setShowModal(true);
                          }}
                          style={{ 
                            fontFamily: '"Lora", serif',
                            borderColor: 'rgb(196, 150, 96)',
                            color: 'rgb(196, 150, 96)'
                          }}
                        >
                          {detailsButtonTranslated}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        <Separator className="my-12" />

        {/* Information sur le projet */}
        <div className="text-center bg-gray-50 p-8 rounded-lg">
          <h3 
            className="text-2xl font-bold mb-4"
            style={{ 
              fontFamily: '"Lora", serif',
              color: 'rgb(196, 150, 96)'
            }}
          >
            {projectTitleTranslated}
          </h3>
          <p 
            className="text-gray-700 leading-relaxed max-w-3xl mx-auto"
            style={{ fontFamily: '"Lora", serif', fontSize: '17px' }}
          >
            {projectDescTranslated}
          </p>
        </div>
      </div>

      {/* Modal de détails */}
      {showModal && selectedFamille && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowModal(false)}
        >
          <div 
            className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Bouton fermer */}
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
            >
              <X size={24} />
            </button>

            {/* Contenu de la modal */}
            <div className="p-8">
              <div className="flex flex-col items-center mb-6">
                {selectedFamille.blason_image_path ? (
                  <OptimizedImage 
                    src={selectedFamille.blason_image_url_full || `${API_BASE}/api/uploads/${selectedFamille.blason_image_path}`}
                    alt={`Blason ${selectedFamille.nom_famille}`}
                    className="w-32 h-32 object-contain mb-4"
                    priority={true}
                    placeholder="blur"
                  />
                ) : (
                  <div className="w-32 h-32 bg-gray-100 rounded flex items-center justify-center mb-4">
                    <Shield size={64} className="text-gray-400" />
                  </div>
                )}
                
                <h2 
                  className="text-3xl font-bold text-center mb-2"
                  style={{ 
                    fontFamily: '"Lora", serif',
                    color: 'rgb(196, 150, 96)'
                  }}
                >
                  {selectedFamille.nom_famille}
                </h2>
              </div>

              {/* Description héraldique */}
              {selectedFamille.description_blason && (
                <div className="mb-6">
                  <h3 
                    className="text-xl font-semibold mb-3"
                    style={{ 
                      fontFamily: '"Lora", serif',
                      color: 'rgb(196, 150, 96)'
                    }}
                  >
                    {modalTitleBlasonTranslated}
                  </h3>
                  <p 
                    className="text-gray-700 leading-relaxed"
                    style={{ fontFamily: '"Lora", serif', fontSize: '16px' }}
                  >
                    {currentLanguage === 'fr' ? selectedFamille.description_blason : (modalDescriptionTranslated || selectedFamille.description_blason)}
                  </p>
                </div>
              )}

              {/* Autres informations */}
              {selectedFamille.autre_info && (
                <div className="mb-6">
                  <h3 
                    className="text-xl font-semibold mb-3"
                    style={{ 
                      fontFamily: '"Lora", serif',
                      color: 'rgb(196, 150, 96)'
                    }}
                  >
                    {modalTitleInfoTranslated}
                  </h3>
                  <div 
                    className="text-gray-700 leading-relaxed whitespace-pre-line"
                    style={{ fontFamily: '"Lora", serif', fontSize: '16px' }}
                  >
                    {currentLanguage === 'fr' ? selectedFamille.autre_info : (modalAutreInfoTranslated || selectedFamille.autre_info)}
                  </div>
                </div>
              )}

              {/* Bouton fermer */}
              <div className="flex justify-center mt-8">
                <Button
                  onClick={() => setShowModal(false)}
                  style={{ 
                    fontFamily: '"Lora", serif',
                    backgroundColor: 'rgb(196, 150, 96)',
                    color: 'white'
                  }}
                >
                  {closeButtonTranslated}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default FamillesBlasons;